// Shared schedule/date-math helpers for Fixed subscription plans.
// Consolidated from three previously-duplicated copies in
// CreateSubscriptionModal.jsx, PurchaseSlider.jsx and SubscriptionDetail.jsx.

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DAY_JS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
export const DAY_FULL = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const ORDINAL = ["", "1st", "2nd", "3rd", "4th", "5th"];

export const joinWithAnd = (arr) => {
  if (!arr || arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  return arr.slice(0, -1).join(", ") + " and " + arr[arr.length - 1];
};

const toDateOnly = (dateLike) => {
  const d = typeof dateLike === "string"
    ? new Date(dateLike.split("T")[0] + "T00:00:00")
    : new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d;
};

const normalizeFixedType = (fixedType) => (fixedType === "HalfYearly" ? "Half Yearly" : fixedType);

export const getFirstOccurrenceOnOrAfter = (startDate, jsDayOfWeek) => {
  const d = toDateOnly(startDate);
  while (d.getDay() !== jsDayOfWeek) d.setDate(d.getDate() + 1);
  return d;
};

export const weekOccurrenceInMonth = (date) => Math.ceil(date.getDate() / 7);

export const getNthWeekdayOfMonth = (year, month, weekday, n) => {
  let count = 0;
  let last = null;
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, month, day);
    if (d.getMonth() !== month) break;
    if (d.getDay() === weekday) {
      last = new Date(d);
      if (++count === n) return last;
    }
  }
  return last;
};

// fixedType may be raw ("HalfYearly", as stored on the backend) or already
// normalized ("Half Yearly") — both are accepted.
export const deriveScheduleMessage = (fixedType, startDate, fixedSchedule) => {
  const type = normalizeFixedType(fixedType);
  if (!startDate || !fixedSchedule || fixedSchedule.length === 0) return null;

  const dayPairs = fixedSchedule.map(day => {
    const firstOcc = getFirstOccurrenceOnOrAfter(startDate, DAY_JS[day]);
    return { day, firstOcc, occ: weekOccurrenceInMonth(firstOcc) };
  });

  if (type === "Monthly") {
    const parts = dayPairs.map(({ day, occ }) => `${ORDINAL[occ]} ${DAY_FULL[day]}`);
    return `Monthly schedule would be the ${joinWithAnd(parts)} of each month`;
  }

  if (type === "Quarterly") {
    const { firstOcc } = dayPairs[0];
    const sm = firstOcc.getMonth();
    const months = [MONTH_NAMES[sm], MONTH_NAMES[(sm + 3) % 12], MONTH_NAMES[(sm + 6) % 12], MONTH_NAMES[(sm + 9) % 12]];
    const parts = dayPairs.map(({ day, occ }) => `${ORDINAL[occ]} ${DAY_FULL[day]}`);
    return `Quarterly schedule would be the ${joinWithAnd(parts)} of ${joinWithAnd(months)} each year`;
  }

  if (type === "Half Yearly") {
    const { firstOcc } = dayPairs[0];
    const sm = firstOcc.getMonth();
    const months = [MONTH_NAMES[sm], MONTH_NAMES[(sm + 6) % 12]];
    const parts = dayPairs.map(({ day, occ }) => `${ORDINAL[occ]} ${DAY_FULL[day]}`);
    return `Half-yearly schedule would be the ${joinWithAnd(parts)} of ${months[0]} and ${months[1]} each year`;
  }

  return null;
};

// Returns up to `count` upcoming visit dates for a Fixed plan, honoring the
// plan's end date if provided.
export const getUpcomingDates = (rawFixedType, startDate, scheduleDays, count = 10, endDate = null) => {
  if (!startDate || !scheduleDays || scheduleDays.length === 0) return [];
  const fixedType = normalizeFixedType(rawFixedType);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const planStart = toDateOnly(startDate);
  const cutoff = planStart > today ? planStart : today;

  const endLimit = endDate
    ? new Date(String(endDate).split("T")[0] + "T23:59:59")
    : null;

  if (fixedType === "Weekly") {
    const all = [];
    for (const day of scheduleDays) {
      let d = getFirstOccurrenceOnOrAfter(cutoff, DAY_JS[day]);
      for (let i = 0; i < count; i++) {
        if (endLimit && d > endLimit) break;
        all.push(new Date(d));
        d = new Date(d); d.setDate(d.getDate() + 7);
      }
    }
    all.sort((a, b) => a - b);
    const seen = new Set();
    return all.filter(d => { const k = d.toDateString(); if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, count);
  }

  const interval = fixedType === "Monthly" ? 1 : fixedType === "Quarterly" ? 3 : 6;
  const dayInfos = scheduleDays.map(day => ({
    jsDay: DAY_JS[day],
    ordinal: weekOccurrenceInMonth(getFirstOccurrenceOnOrAfter(planStart, DAY_JS[day])),
  }));

  const monthsDiff = (cutoff.getFullYear() - planStart.getFullYear()) * 12 + cutoff.getMonth() - planStart.getMonth();
  const skip = Math.max(0, Math.floor(monthsDiff / interval) - 1);
  let month = planStart.getMonth() + skip * interval;
  let year = planStart.getFullYear() + Math.floor(month / 12);
  month = ((month % 12) + 12) % 12;

  const result = [];
  let guard = 0;
  let hitEnd = false;
  while (result.length < count && !hitEnd && guard++ < 200) {
    for (const { jsDay, ordinal } of dayInfos) {
      const d = getNthWeekdayOfMonth(year, month, jsDay, ordinal);
      if (d && d >= cutoff) {
        if (endLimit && d > endLimit) { hitEnd = true; break; }
        result.push(new Date(d));
      }
    }
    month += interval;
    if (month >= 12) { year += Math.floor(month / 12); month %= 12; }
  }
  result.sort((a, b) => a - b);
  return result.slice(0, count);
};

// Returns the Date objects within [year, monthIndex0] whose weekday matches
// one of fixedSchedule's day abbreviations (e.g. ["Mon","Wed","Fri"]).
// Used to compute "classes scheduled this calendar month" for pro-rating —
// independent of the plan's start date, since a Weekly schedule simply
// repeats every week.
export const countScheduledOccurrencesInMonth = (fixedSchedule, year, monthIndex0) => {
  const weekdaySet = new Set((fixedSchedule || []).map(day => DAY_JS[day]).filter(v => v !== undefined));
  const dates = [];
  if (weekdaySet.size === 0) return dates;
  const daysInMonth = new Date(year, monthIndex0 + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, monthIndex0, day);
    if (weekdaySet.has(d.getDay())) dates.push(d);
  }
  return dates;
};
